/**
 * A module's comment thread: append-only, newest at the bottom, with @mentions picked from the
 * people who can edit (arrow keys and Enter/Tab in the picker, Enter to send, Shift+Enter for a
 * new line). The module keeps the comments — its own table, its own route — and passes them in;
 * this draws them and the box.
 *
 * Sending waits for the module: the box clears only once `onSend` says the comment landed, so a
 * refusal keeps the text, and it can't be sent twice while one is on its way.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { cn } from '../lib/utils';
import { businessStamp } from '../lib/business-time';

/** Someone a comment can name: the module's directory entry. */
export interface ThreadPerson {
  id: string;
  name: string;
  /** Only active people are offered in the @ picker; former ones still show by name. */
  active: boolean;
}

export interface ThreadComment {
  id: string;
  authorUserId: string;
  body: string;
  createdAt: string;
}

/** Splits a body into plain runs and `@Name` runs for the people it mentions. */
export function splitMentions(
  body: string,
  names: readonly string[],
): { text: string; mention: boolean }[] {
  const escaped = [...names]
    .sort((a, b) => b.length - a.length)
    .map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (escaped.length === 0) return [{ text: body, mention: false }];
  const re = new RegExp(`(@(?:${escaped.join('|')}))`, 'g');
  return body
    .split(re)
    .filter((part) => part !== '')
    .map((part) => ({
      text: part,
      mention: part.startsWith('@') && names.includes(part.slice(1)),
    }));
}

/** The `@partial` being typed at the end of the message, if any. */
export function mentionQuery(message: string): string | null {
  const match = message.match(/(?:^|\s)@([\p{L}'-]*)$/u);
  return match ? match[1]! : null;
}

/** Active people whose name, or a word of it, starts with the query. */
export function mentionSuggestions<P extends ThreadPerson>(
  directory: readonly P[],
  query: string,
): P[] {
  const lower = query.toLowerCase();
  return directory.filter(
    (person) =>
      person.active &&
      (person.name.toLowerCase().startsWith(lower) ||
        person.name
          .toLowerCase()
          .split(' ')
          .some((word) => word.startsWith(lower))),
  );
}

export function CommentThread<C extends ThreadComment>({
  comments,
  directory,
  canComment,
  emptyText,
  postingAs,
  renderTag,
  onSend,
  className,
}: {
  comments: readonly C[];
  directory: readonly ThreadPerson[];
  canComment: boolean;
  /** What an empty thread says. */
  emptyText: string;
  /** The commenter's name, shown under the box; omit for no line. */
  postingAs?: string;
  /** Something at the right of a comment's header (product: the gate it was said in). */
  renderTag?: (comment: C) => ReactNode;
  /** Resolves true once posted; the box clears only then. */
  onSend: (body: string, mentions: string[]) => Promise<boolean>;
  className?: string;
}) {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  // The state drives the button; the ref is the guard, since two clicks in one tick both see
  // the state from before the first.
  const inFlight = useRef(false);
  const [highlight, setHighlight] = useState(0);
  const listRef = useRef<HTMLOListElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const nameOf = (id: string) => directory.find((person) => person.id === id)?.name ?? 'Someone';
  const names = useMemo(() => directory.map((person) => person.name), [directory]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [comments.length]);

  const query = mentionQuery(message);
  const suggestions = query === null ? [] : mentionSuggestions(directory, query);
  const highlighted = Math.min(highlight, suggestions.length - 1);

  const pick = (person: ThreadPerson) => {
    setMessage(message.replace(/@([\p{L}'-]*)$/u, `@${person.name} `));
    setHighlight(0);
    inputRef.current?.focus();
  };

  const send = async () => {
    const body = message.trim();
    if (body === '' || inFlight.current) return;
    const mentions = directory
      .filter((person) => body.includes(`@${person.name}`))
      .map((person) => person.id);
    inFlight.current = true;
    setSending(true);
    try {
      if (await onSend(body, mentions)) setMessage('');
    } finally {
      inFlight.current = false;
      setSending(false);
    }
  };

  return (
    <div className={cn('flex flex-col gap-2.5', className)}>
      <h4 className="flex items-baseline gap-2">
        <span className="text-[15px] font-semibold">Comments</span>
        <span className="text-sm text-[hsl(var(--ns-boulder))]">{comments.length}</span>
      </h4>
      <ol
        ref={listRef}
        className="flex max-h-[420px] min-h-[120px] flex-col gap-2.5 overflow-y-auto pr-1"
      >
        {comments.length === 0 && (
          <li className="py-2 text-sm text-[hsl(var(--ns-boulder))]">{emptyText}</li>
        )}
        {comments.map((comment) => (
          <li
            key={comment.id}
            className="flex flex-col gap-1 rounded-xl border border-[hsl(var(--ns-acai)/0.08)] bg-background px-3 py-2.5"
          >
            <p className="flex items-center gap-1.5 text-[13px]">
              <span className="font-semibold">{nameOf(comment.authorUserId)}</span>
              <span className="text-[hsl(var(--ns-boulder))]">
                {businessStamp(comment.createdAt)}
              </span>
              {renderTag && <span className="ml-auto">{renderTag(comment)}</span>}
            </p>
            <p className="whitespace-pre-wrap text-pretty text-sm leading-[1.4]">
              {splitMentions(comment.body, names).map((part, i) =>
                part.mention ? (
                  <span
                    key={i}
                    className="rounded px-[3px] font-semibold text-primary bg-[hsl(var(--ns-bud)/0.3)]"
                  >
                    {part.text}
                  </span>
                ) : (
                  <span key={i}>{part.text}</span>
                ),
              )}
            </p>
          </li>
        ))}
      </ol>

      {canComment && (
        <div className="relative flex flex-col gap-2 rounded-xl border border-[hsl(var(--ns-acai)/0.14)] bg-background p-2.5">
          {suggestions.length > 0 && (
            <ul className="absolute inset-x-2 bottom-[calc(100%+6px)] z-10 flex max-h-48 flex-col overflow-y-auto rounded-[10px] border border-[hsl(var(--ns-acai)/0.12)] bg-background p-1 shadow-[0_8px_24px_rgba(44,24,71,0.08)]">
              {suggestions.map((person, i) => (
                <li key={person.id}>
                  <button
                    type="button"
                    onMouseDown={(event) => {
                      // Keep the focus in the box.
                      event.preventDefault();
                      pick(person);
                    }}
                    className={cn(
                      'w-full rounded-[7px] px-2.5 py-2 text-left text-sm hover:bg-[hsl(var(--ns-pebble)/0.4)]',
                      i === highlighted && 'bg-[hsl(var(--ns-pebble)/0.4)]',
                    )}
                  >
                    {person.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <textarea
            ref={inputRef}
            value={message}
            rows={2}
            placeholder="Write a message. Type @ to tag someone"
            aria-label="Comment"
            onChange={(event) => {
              setMessage(event.target.value);
              setHighlight(0);
            }}
            onKeyDown={(event) => {
              if (suggestions.length > 0) {
                if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                  event.preventDefault();
                  const count = suggestions.length;
                  setHighlight(
                    (current) => (current + (event.key === 'ArrowDown' ? 1 : count - 1)) % count,
                  );
                  return;
                }
                if (event.key === 'Enter' || event.key === 'Tab') {
                  event.preventDefault();
                  pick(suggestions[highlighted]!);
                  return;
                }
              }
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                send();
              }
            }}
            className="resize-none bg-transparent text-sm leading-[1.4] outline-none placeholder:text-[hsl(var(--ns-boulder))]"
          />
          <div className="flex items-center gap-2">
            {postingAs !== undefined && (
              <>
                <span className="whitespace-nowrap text-xs text-[hsl(var(--ns-boulder))]">
                  Posting as
                </span>
                <span className="min-w-0 truncate text-[13px] font-semibold">{postingAs}</span>
              </>
            )}
            <span className="flex-1" />
            <button
              type="button"
              onClick={() => {
                setMessage(
                  (current) => current + (current !== '' && !/\s$/.test(current) ? ' @' : '@'),
                );
                inputRef.current?.focus();
              }}
              title="Tag someone"
              aria-label="Tag someone"
              className="size-8 rounded-lg border border-[hsl(var(--ns-acai)/0.14)] bg-background text-[15px] font-semibold"
            >
              @
            </button>
            <button
              type="button"
              onClick={send}
              disabled={sending || message.trim() === ''}
              className="h-8 rounded-lg bg-primary px-3.5 text-sm text-[hsl(var(--ns-unbleached))] hover:bg-[hsl(var(--ns-black))] disabled:opacity-50 disabled:hover:bg-primary"
            >
              Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
